import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "nl_tour_dismissed"

const STEPS = [
  {
    id: "palette",
    title: "Ajouter un élément",
    body: "Cliquez sur un type de bloc (Titre, Encart, Texte, Image ou Séparateur) pour l’ajouter à votre newsletter."
  },
  {
    id: "canvas",
    title: "Votre newsletter",
    body: "Vos blocs s’affichent ici, dans un aperçu fidèle au rendu final envoyé à vos clients."
  },
  {
    id: "edit-block",
    title: "Éditer ou supprimer un bloc",
    body: "Survolez un bloc pour faire apparaître sa barre d’outils, puis cliquez sur « Éditer » pour modifier son contenu ou sur la corbeille pour le supprimer."
  },
  {
    id: "reorder",
    title: "Réordonner les blocs",
    body: "Saisissez cette poignée pour glisser-déposer un bloc et changer sa position dans la newsletter."
  },
  {
    id: "actions",
    title: "Aperçu, export et réinitialisation",
    body: "Prévisualisez votre newsletter, téléchargez-la en PDF, ou réinitialisez-la au modèle EOS du mois pour repartir de zéro."
  },
  {
    id: "autosave",
    title: "Sauvegarde automatique",
    body: "Toutes vos modifications sont enregistrées automatiquement : vous n’avez rien à valider."
  }
]

export default class extends Controller {
  connect() {
    if (this.dismissed) return

    this.currentStepIndex = 0
    this.highlightedElement = null
    this.demoToolbarElement = null
    this.boundReposition = this.reposition.bind(this)
    this.boundNext = this.next.bind(this)
    this.boundClose = this.close.bind(this)
    this.boundDismiss = this.dismiss.bind(this)

    requestAnimationFrame(() => {
      this.build()
      this.showStep(0)
    })
  }

  disconnect() {
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
    this.cleanupHighlight()

    if (this.nextButton) this.nextButton.removeEventListener("click", this.boundNext)
    if (this.closeButton) this.closeButton.removeEventListener("click", this.boundClose)
    if (this.stopLink) this.stopLink.removeEventListener("click", this.boundDismiss)

    if (this.overlay) this.overlay.remove()
    if (this.card) this.card.remove()
  }

  next(event) {
    if (event) event.preventDefault()

    this.cleanupStepEffects()

    if (this.currentStepIndex >= STEPS.length - 1) {
      this.close()
      return
    }

    this.showStep(this.currentStepIndex + 1)
  }

  dismiss(event) {
    if (event) event.preventDefault()
    this.markDismissed()
    this.close()
  }

  get dismissed() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === "1"
    } catch (error) {
      return false
    }
  }

  markDismissed() {
    try {
      window.localStorage.setItem(STORAGE_KEY, "1")
    } catch (error) {
      // localStorage indisponible (mode privé, quota) : on ignore silencieusement
    }
  }

  close(event) {
    if (event) event.preventDefault()
    this.cleanupStepEffects()
    this.cleanupHighlight()

    if (this.overlay) this.overlay.classList.add("hidden")
    if (this.card) this.card.classList.add("hidden")

    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
  }

  build() {
    this.overlay = document.createElement("div")
    this.overlay.className = "fixed inset-0 z-[120] bg-slate-950/45"

    this.card = document.createElement("div")
    this.card.className = "fixed z-[130] hidden w-[320px] border border-slate-200 bg-white p-5 shadow-2xl"

    this.card.innerHTML = `
      <div class="flex items-start justify-between gap-4">
        <div>
          <div class="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500" data-guided-tour-role="counter"></div>
          <h3 class="mt-2 text-base font-semibold text-slate-900" data-guided-tour-role="title"></h3>
        </div>

        <button type="button"
                class="shrink-0 text-sm font-medium text-slate-500 transition hover:text-slate-700"
                data-guided-tour-role="close">
          Fermer
        </button>
      </div>

      <p class="mt-3 text-sm leading-6 text-slate-600" data-guided-tour-role="body"></p>

      <div class="mt-5 flex items-center justify-between gap-4">
        <a href="#"
           class="text-sm text-slate-500 underline underline-offset-2 transition hover:text-slate-700"
           data-guided-tour-role="stop">
          Ne plus afficher ce tutoriel
        </a>

        <button type="button"
                class="inline-flex items-center border border-transparent bg-oxford-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-oxford-800"
                data-guided-tour-role="next">
          Suivant
        </button>
      </div>
    `

    document.body.appendChild(this.overlay)
    document.body.appendChild(this.card)

    this.counterElement = this.card.querySelector('[data-guided-tour-role="counter"]')
    this.titleElement = this.card.querySelector('[data-guided-tour-role="title"]')
    this.bodyElement = this.card.querySelector('[data-guided-tour-role="body"]')
    this.closeButton = this.card.querySelector('[data-guided-tour-role="close"]')
    this.nextButton = this.card.querySelector('[data-guided-tour-role="next"]')
    this.stopLink = this.card.querySelector('[data-guided-tour-role="stop"]')

    this.closeButton.addEventListener("click", this.boundClose)
    this.nextButton.addEventListener("click", this.boundNext)
    this.stopLink.addEventListener("click", this.boundDismiss)
  }

  showStep(index) {
    const step = STEPS[index]
    if (!step) return

    const element = this.resolveStepElement(step.id)
    if (!element) {
      if (index >= STEPS.length - 1) {
        this.close()
      } else {
        this.showStep(index + 1)
      }
      return
    }

    this.currentStepIndex = index
    this.cleanupHighlight()
    this.applyStepEffects(step.id)

    element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })

    window.setTimeout(() => {
      this.highlight(element)
      this.counterElement.textContent = `Étape ${index + 1} / ${STEPS.length}`
      this.titleElement.textContent = step.title
      this.bodyElement.textContent = step.body
      this.nextButton.textContent = index === STEPS.length - 1 ? "Terminer" : "Suivant"

      this.overlay.classList.remove("hidden")
      this.card.classList.remove("hidden")
      this.reposition()
      this.nextButton.focus()

      window.addEventListener("resize", this.boundReposition)
      window.addEventListener("scroll", this.boundReposition, true)
    }, 180)
  }

  resolveStepElement(stepId) {
    if (stepId === "edit-block") {
      return this.firstBlock()
    }

    if (stepId === "reorder") {
      const block = this.firstBlock()
      return block ? block.querySelector(".nl-handle") : null
    }

    if (stepId === "actions") {
      return document.querySelector("#newsletter-title .items-end.justify-end")
    }

    return document.querySelector(`[data-guided-tour-step="${stepId}"]`)
  }

  applyStepEffects(stepId) {
    if (stepId !== "edit-block" && stepId !== "reorder") return

    const block = this.firstBlock()
    if (!block) return

    const toolbar = block.querySelector(".nl-toolbar")
    if (!toolbar) return

    this.demoToolbarElement = toolbar
    toolbar.style.opacity = "1"
  }

  cleanupStepEffects() {
    if (this.demoToolbarElement) {
      this.demoToolbarElement.style.opacity = ""
      this.demoToolbarElement = null
    }
  }

  firstBlock() {
    return document.querySelector("#newsletter-fields .nl-field")
  }

  highlight(element) {
    this.highlightedElement = element
    // Certains éléments (ex. .nl-field) sont déjà `relative` : ne pas le retirer au cleanup
    this.addedRelativeClass = !element.classList.contains("relative")
    if (this.addedRelativeClass) element.classList.add("relative")
    element.classList.add("z-[125]", "ring-4", "ring-oxford-900", "ring-offset-4", "ring-offset-white")
  }

  cleanupHighlight() {
    if (!this.highlightedElement) return

    this.highlightedElement.classList.remove("z-[125]", "ring-4", "ring-oxford-900", "ring-offset-4", "ring-offset-white")
    if (this.addedRelativeClass) this.highlightedElement.classList.remove("relative")
    this.highlightedElement = null
    this.addedRelativeClass = false
  }

  reposition() {
    if (!this.highlightedElement || !this.card) return

    const rect = this.highlightedElement.getBoundingClientRect()
    const cardRect = this.card.getBoundingClientRect()
    const margin = 16
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let top = rect.bottom + margin
    if (top + cardRect.height > viewportHeight - margin) {
      top = rect.top - cardRect.height - margin
    }

    top = Math.max(margin, top)

    let left = rect.left
    if (left + cardRect.width > viewportWidth - margin) {
      left = viewportWidth - cardRect.width - margin
    }

    left = Math.max(margin, left)

    this.card.style.top = `${top}px`
    this.card.style.left = `${left}px`
  }
}
